import React from 'react';
import * as Linking from 'expo-linking';
import { APITypes } from '../../../../primary-record-types';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import Text from '../../../v2BaseComponents/Text';
import { Checkbox } from '../../../v2BaseComponents/Inputs';
import { OrganizationInvitationDefinition } from '../../../../primary-record-types/OrganizationTypes';
import useOrganizationInvitationDefinitions, {
    isConsentCheckboxInput,
} from '../../../../hooks/api/useOrganizationInvitationDefinitions';
import Icon from '../../../v2BaseComponents/Icon';
import IconButton from '../../../v2BaseComponents/IconButton';
import { noAccessAppPermissions } from '../../../../utils/defaultPermissions';
import PermissionsInput from '../../PermissionsInput';

export type DetailsContentOwnProps = {
    invitationDefinition: APITypes.organizationService.GetInvitationDefinitionResponse;
};

export type DetailsContentProps = DetailsContentOwnProps;

const FileSection: React.FC<{
    file: OrganizationInvitationDefinition['files'][0];
    organizationInvitationDefinitionId: string;
}> = ({ file, organizationInvitationDefinitionId }) => {
    const { getDownloadUrl, isLoadingDownloadUrl } = useOrganizationInvitationDefinitions({
        onFetchedFileDownloadUrl: (downloadUrl) => {
            Linking.openURL(downloadUrl);
        },
    });

    return (
        <Stack horizontal fullWidth justify="space-between" align="center" space={2}>
            <Stack horizontal justify="flex-start" align="center" space={2}>
                <Icon name="FileText" size="selected-file-icon" />
                <Stack vertical fullHeight justify="center" align="flex-start">
                    <Text variant="body-1">{file.name}</Text>
                    <Text variant="caption-1">{file.description}</Text>
                </Stack>
            </Stack>
            <IconButton
                icon="Download"
                variant="transparent"
                onPress={() =>
                    getDownloadUrl({
                        invitationDefinitionId: organizationInvitationDefinitionId,
                        fileId: file.fileId,
                    })
                }
                isDisabled={isLoadingDownloadUrl}
            />
        </Stack>
    );
};

const DetailsContent: React.FC<DetailsContentProps> = (props) => {
    return (
        <Stack vertical fullWidth space={contentStackSpacing}>
            <Text variant="h4">{props.invitationDefinition.name}</Text>
            {props.invitationDefinition.message ? (
                <Text variant="body-1">{props.invitationDefinition.message}</Text>
            ) : null}
            <PermissionsInput
                value={props.invitationDefinition.requestedProfilePermissions || noAccessAppPermissions}
                isDisabled
                onChange={() => {}}
                showEditCollaboratorWarning
                initialAppPermissions={props.invitationDefinition.requestedProfilePermissions || noAccessAppPermissions}
            />
            <Stack vertical fullWidth space={2}>
                {props.invitationDefinition.files.map((file, index) => (
                    <FileSection
                        key={index}
                        file={file}
                        organizationInvitationDefinitionId={
                            props.invitationDefinition.organizationInvitationDefinitionId
                        }
                    />
                ))}
            </Stack>
            <Stack vertical fullWidth space={2}>
                {props.invitationDefinition.inputs.filter(isConsentCheckboxInput).map((input, index) => (
                    <Checkbox key={index} title={input.label} value isDisabled onChange={() => {}} />
                ))}
            </Stack>
        </Stack>
    );
};

export default React.memo(DetailsContent);
